import React, { useMemo, useState } from "react";

import {
  faArrowDown,
  faArrowsUpDown,
  faArrowUp,
  faCog,
  faPlus,
  faRefresh,
} from "@fortawesome/pro-regular-svg-icons";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper, HeaderContext } from "@tanstack/react-table";
import { isEqual } from "lodash-es";
import { Link } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { CreateOperationModal } from "src/adminPanel/modulesV2/CreateOperationModal";
import { EnableModuleModal } from "src/adminPanel/modulesV2/EnableModuleModal";
import { ModuleOperationsDetail } from "src/adminPanel/modulesV2/ModuleOperationsDetail";
import {
  ModuleVersionBars,
  VersionFilter,
} from "src/adminPanel/modulesV2/ModuleVersionBars";
import { SpecDiffModal } from "src/adminPanel/modulesV2/SpecDiffModal";
import {
  enrichModulesForSearch,
  getModuleOperationStatus,
  getOperationStatusLabel,
  getOperationStatusPillVariant,
  MODULE_SEARCH_OPTIONS,
  moduleHasOperationMatch,
  OPERATION_STATUS_FILTER_OPTIONS,
} from "src/adminPanel/modulesV2/utils";
import { Module, moduleKeys } from "src/api/modulesApi";
import { Button } from "src/base-components/Button";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { FixedSimpleDropdown } from "src/base-components/FixedSimpleDropdown";
import { Input } from "src/base-components/Input";
import { Pill } from "src/base-components/Pill";
import { CustomPopover } from "src/base-components/Popover";
import { Cell, TableComp } from "src/base-components/Table";
import {
  ModuleKind,
  ModuleOperationStatus,
  WorkspaceDataplane,
} from "src/clients/flow-api";
import { ObjectDetailPane } from "src/dataTable/ObjectDetailPane";
import { Icon } from "src/design-system/Icon";
import { Modal, useModal } from "src/design-system/Modal";
import { useFuseSearch } from "src/utils/useFuseSearch";

type Props = {
  modules: Module[];
  searchTerm: string;
  workspaceMap: Map<string, { name: string; orgId: string; slug: string }>;
  orgMap: Map<string, string>;
  moduleKind: ModuleKind;
  workspaces: WorkspaceDataplane[];
  agentWorkspaceIds: Set<string>;
};

const columnHelper = createColumnHelper<Module>();

type FilterOption = {
  value: string;
  label: string;
};

type SortableHeaderProps = {
  info: HeaderContext<Module, unknown>;
  children: React.ReactNode;
  showFilter?: boolean;
  filterOptions?: FilterOption[];
};

const SortableHeader: React.FC<SortableHeaderProps> = ({
  info,
  children,
  showFilter = false,
  filterOptions,
}) => {
  const column = info.column;
  const isSorted = column.getIsSorted();
  const canSort = column.getCanSort();
  const filterValue = (column.getFilterValue() as string) ?? "";
  let sortIcon = faArrowsUpDown;
  if (isSorted === "asc") {
    sortIcon = faArrowUp;
  } else if (isSorted === "desc") {
    sortIcon = faArrowDown;
  }

  return (
    <div className="flex flex-col gap-2 px-4 py-3">
      <button
        className={twJoin(
          "flex items-center gap-2 whitespace-nowrap text-left font-inter-medium-12px text-gray-800",
          canSort && "cursor-pointer hover:text-gray-900",
        )}
        disabled={!canSort}
        type="button"
        onClick={() => canSort && column.toggleSorting()}
      >
        <span>{children}</span>
        {canSort && <Icon icon={sortIcon} size="2xs" />}
      </button>
      {filterOptions && column.getCanFilter() && (
        <div className="min-w-[120px]" onClick={(e) => e.stopPropagation()}>
          <FixedSimpleDropdown
            elements={[
              { key: "", value: "All" },
              ...filterOptions.map(({ value, label }) => ({
                key: value,
                value: label,
              })),
            ]}
            itemsWidth="w-36"
            placeholder="All"
            selectedKey={filterValue}
            onSelect={(key) => column.setFilterValue(key || undefined)}
          />
        </div>
      )}
      {showFilter && !filterOptions && column.getCanFilter() && (
        <div className="min-w-[120px]">
          <Input
            placeholder="Filter..."
            type="text"
            value={filterValue}
            onChange={(e) => column.setFilterValue(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

const ObjectPreview: React.FC<{ value: Record<string, unknown> }> = ({
  value,
}) => {
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <CustomPopover
        button={
          <Pill size="sm" variant="gray">
            <Pill.Text fontType="code">{"{…}"}</Pill.Text>
          </Pill>
        }
        className="p-3"
        placement="bottom-start"
      >
        <ObjectDetailPane
          defaultInspectDepth={2}
          innerDimensionClass="max-h-80 max-w-md"
          jsonObject={value as Record<string, unknown>}
          speakPython={false}
        />
      </CustomPopover>
    </span>
  );
};

const ConfigValueCell: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === undefined || value === null) {
    return (
      <span className="font-inter-normal-13px text-gray-400 italic">—</span>
    );
  }
  if (typeof value === "boolean") {
    return (
      <Pill variant={value ? "green" : "red"}>
        <Pill.Text fontType="text">{String(value)}</Pill.Text>
      </Pill>
    );
  }
  if (typeof value === "object") {
    return <ObjectPreview value={value as Record<string, unknown>} />;
  }
  return (
    <span className="font-inter-normal-13px text-gray-900">
      {String(value)}
    </span>
  );
};

const getSpecStatus = (
  mod: Module,
): { variant: "green" | "yellow"; label: string } => {
  if (mod.spec === null) {
    return { variant: "yellow", label: "Pending" };
  }
  if (isEqual(mod.spec, mod.desiredSpec)) {
    return { variant: "green", label: "In Sync" };
  }
  return { variant: "yellow", label: "Updating" };
};

export const ModuleKindSection: React.FC<Props> = ({
  modules,
  searchTerm,
  workspaceMap,
  orgMap,
  moduleKind,
  workspaces,
  agentWorkspaceIds,
}) => {
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [versionFilter, setVersionFilter] = useState<VersionFilter>(null);
  const [prevSearchTerm, setPrevSearchTerm] = useState(searchTerm);
  if (searchTerm !== prevSearchTerm) {
    setPrevSearchTerm(searchTerm);
    setVersionFilter(null);
  }

  const existingWorkspaceIds = useMemo(
    () => new Set(modules.map((m) => m.workspaceId)),
    [modules],
  );
  const {
    isOpen: isDiffOpen,
    openModal: openDiffModal,
    closeModal: closeDiffModal,
    afterLeave: afterDiffLeave,
    data: diffModule,
  } = useModal<Module>();

  const {
    isOpen: isEnableOpen,
    openModal: openEnableModal,
    closeModal: closeEnableModal,
    afterLeave: afterEnableLeave,
  } = useModal();

  const {
    isOpen: isCreateOpOpen,
    openModal: openCreateOpModal,
    closeModal: closeCreateOpModal,
    afterLeave: afterCreateOpLeave,
    data: createOpModule,
  } = useModal<Module>();

  const {
    isOpen: isOpsDetailOpen,
    openModal: openOpsDetailModal,
    closeModal: closeOpsDetailModal,
    afterLeave: afterOpsDetailLeave,
    data: opsDetailModule,
  } = useModal<Module>();

  const currentOpsDetailModule = opsDetailModule
    ? (modules.find((m) => m.id === opsDetailModule.id) ?? opsDetailModule)
    : undefined;

  const searchableModules = useMemo(
    () => enrichModulesForSearch(modules, workspaceMap, orgMap),
    [modules, workspaceMap, orgMap],
  );
  const fuseSearch = useFuseSearch(searchableModules, MODULE_SEARCH_OPTIONS);
  const searchFilteredModules = useMemo(
    () => fuseSearch(searchTerm),
    [fuseSearch, searchTerm],
  );

  const filteredModules = useMemo(() => {
    if (!versionFilter) return searchFilteredModules;
    return searchFilteredModules.filter((mod) => {
      if (versionFilter.source === "not_deployed") {
        return mod.spec === null;
      }
      if (versionFilter.source === "desired") {
        return mod.desiredSpec.version === versionFilter.version;
      }
      return mod.spec?.version === versionFilter.version;
    });
  }, [searchFilteredModules, versionFilter]);

  const allConfigKeys = useMemo(() => {
    const keysSet = new Set<string>();
    modules.map((mod) => {
      Object.keys((mod.spec ?? mod.desiredSpec).config).map((key) =>
        keysSet.add(key),
      );
      return null;
    });
    return [...keysSet].sort();
  }, [modules]);

  const getModuleContext = (mod: Module) => {
    const ws = workspaceMap.get(mod.workspaceId);
    return {
      workspaceName: ws?.name,
      orgName: ws ? orgMap.get(ws.orgId) : undefined,
    };
  };

  const opsDetailContext = currentOpsDetailModule
    ? getModuleContext(currentOpsDetailModule)
    : { workspaceName: undefined, orgName: undefined };

  const columns = useMemo(
    () => [
      columnHelper.accessor(
        (row) => {
          const ws = workspaceMap.get(row.workspaceId);
          const orgName = ws ? orgMap.get(ws.orgId) : undefined;
          return ws ? `${orgName ?? ""} ${ws.orgId}` : "";
        },
        {
          id: "organization",
          header: (info) => (
            <SortableHeader info={info} showFilter>
              Organization
            </SortableHeader>
          ),
          cell: (info) => {
            const mod = info.row.original;
            const ws = workspaceMap.get(mod.workspaceId);
            const orgName = ws ? orgMap.get(ws.orgId) : undefined;
            return (
              <Cell className="px-4" info={info}>
                {ws ? (
                  <span className="inline-flex items-center gap-1">
                    <Link
                      className="text-blue-600 transition-colors hover:text-blue-800 hover:underline"
                      title={`Click to find organization ${orgName ?? ws.orgId} in the Workspaces tab`}
                      to={`/admin-panel/organizations?search=${ws.orgId}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="font-inter-normal-13px">
                        {orgName ?? ws.orgId.slice(0, 8)}
                      </span>
                    </Link>
                    <span onClick={(e) => e.stopPropagation()}>
                      <CopyTextIcon
                        feedback="inline"
                        size="xs"
                        tooltip="Copy organization ID"
                        value={ws.orgId}
                      />
                    </span>
                  </span>
                ) : (
                  <span className="font-inter-normal-13px text-gray-500">
                    —
                  </span>
                )}
              </Cell>
            );
          },
          enableSorting: true,
          enableColumnFilter: true,
          filterFn: (row, _columnId, filterValue: string) => {
            const mod = row.original;
            const ws = workspaceMap.get(mod.workspaceId);
            if (!ws) return false;
            const orgName = orgMap.get(ws.orgId) ?? "";
            const lower = filterValue.toLowerCase();
            return (
              orgName.toLowerCase().includes(lower) ||
              ws.orgId.toLowerCase().includes(lower)
            );
          },
          meta: { displayName: "Organization" },
        },
      ),
      columnHelper.accessor(
        (row) => {
          const ws = workspaceMap.get(row.workspaceId);
          return ws?.name ?? row.workspaceId;
        },
        {
          id: "workspace",
          header: (info) => (
            <SortableHeader info={info} showFilter>
              Workspace
            </SortableHeader>
          ),
          cell: (info) => {
            const mod = info.row.original;
            const ws = workspaceMap.get(mod.workspaceId);
            return (
              <Cell className="px-4" info={info}>
                <div className="flex items-center gap-1">
                  <div className="flex flex-col">
                    <Link
                      className="text-blue-600 transition-colors hover:text-blue-800 hover:underline"
                      title="Click to find this workspace in the Workspaces tab"
                      to={`/admin-panel/organizations?search=${mod.workspaceId}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="font-inter-normal-13px">
                        {ws?.name ?? mod.workspaceId.slice(0, 8)}
                      </span>
                    </Link>
                    {ws && (
                      <span className="font-inter-normal-12px text-gray-500">
                        {ws.slug}
                      </span>
                    )}
                  </div>
                  <span onClick={(e) => e.stopPropagation()}>
                    <CopyTextIcon
                      feedback="inline"
                      size="xs"
                      tooltip="Copy workspace ID"
                      value={mod.workspaceId}
                    />
                  </span>
                </div>
              </Cell>
            );
          },
          enableSorting: true,
          enableColumnFilter: true,
          filterFn: (row, _columnId, filterValue: string) => {
            const mod = row.original;
            const ws = workspaceMap.get(mod.workspaceId);
            const lower = filterValue.toLowerCase();
            return (
              (ws?.name.toLowerCase().includes(lower) ?? false) ||
              (ws?.slug.toLowerCase().includes(lower) ?? false) ||
              mod.workspaceId.toLowerCase().includes(lower)
            );
          },
          meta: { displayName: "Workspace" },
        },
      ),
      columnHelper.accessor("id", {
        id: "moduleId",
        header: (info) => (
          <SortableHeader info={info} showFilter>
            Module ID
          </SortableHeader>
        ),
        cell: (info) => (
          <Cell className="px-4" info={info}>
            <span className="inline-flex items-center gap-1">
              <span className="font-code-12 text-gray-900">
                {info.getValue().slice(0, 8)}
              </span>
              <span onClick={(e) => e.stopPropagation()}>
                <CopyTextIcon
                  feedback="inline"
                  size="xs"
                  tooltip="Copy module ID"
                  value={info.getValue()}
                />
              </span>
            </span>
          </Cell>
        ),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: "includesString",
        meta: { displayName: "Module ID" },
      }),
      columnHelper.accessor((row) => getModuleOperationStatus(row) ?? "", {
        id: "operationStatus",
        header: (info) => (
          <SortableHeader
            filterOptions={OPERATION_STATUS_FILTER_OPTIONS}
            info={info}
          >
            Op Status
          </SortableHeader>
        ),
        cell: (info) => {
          const status = getModuleOperationStatus(info.row.original);
          if (!status) {
            return (
              <Cell className="px-4" info={info}>
                <span className="font-inter-normal-13px text-gray-400">—</span>
              </Cell>
            );
          }
          return (
            <Cell className="px-4" info={info}>
              <Pill variant={getOperationStatusPillVariant(status)}>
                <Pill.Text>{getOperationStatusLabel(status)}</Pill.Text>
              </Pill>
            </Cell>
          );
        },
        sortingFn: (rowA, rowB) => {
          const statuses = Object.values(ModuleOperationStatus);
          const getOrder = (mod: Module): number => {
            const status = getModuleOperationStatus(mod);
            return status ? statuses.indexOf(status) : statuses.length;
          };
          return getOrder(rowA.original) - getOrder(rowB.original);
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValue: string) =>
          getModuleOperationStatus(row.original) === filterValue,
        meta: { displayName: "Op Status" },
      }),
      columnHelper.accessor(
        (row) => (row.spec ?? row.desiredSpec).profile_level,
        {
          id: "profileLevel",
          header: (info) => (
            <SortableHeader info={info}>Profile Level</SortableHeader>
          ),
          cell: (info) => {
            const level = info.getValue();
            const variant: "red" | "green" | "yellow" | "orange" | "indigo" =
              level < 0
                ? "red"
                : level === 0
                  ? "green"
                  : level === 1
                    ? "yellow"
                    : level === 2
                      ? "orange"
                      : "indigo";
            return (
              <Cell className="px-4" info={info}>
                <Pill variant={variant}>
                  <Pill.Text fontType="text">Level {level}</Pill.Text>
                </Pill>
              </Cell>
            );
          },
          enableSorting: true,
          enableColumnFilter: false,
          meta: { displayName: "Profile Level" },
        },
      ),
      columnHelper.accessor((row) => (row.spec ?? row.desiredSpec).is_active, {
        id: "active",
        header: (info) => <SortableHeader info={info}>Enabled</SortableHeader>,
        cell: (info) => (
          <Cell className="px-4" info={info}>
            <Pill variant={info.getValue() ? "green" : "red"}>
              <Pill.Text fontType="text">
                {info.getValue() ? "Enabled" : "Disabled"}
              </Pill.Text>
            </Pill>
          </Cell>
        ),
        enableSorting: true,
        enableColumnFilter: false,
        meta: { displayName: "Enabled" },
      }),
      columnHelper.display({
        id: "specStatus",
        header: (info) => <SortableHeader info={info}>Spec</SortableHeader>,
        cell: (info) => {
          const mod = info.row.original;
          const { variant, label } = getSpecStatus(mod);
          return (
            <Cell className="px-4" info={info}>
              <button
                className="cursor-pointer"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openDiffModal(mod);
                }}
              >
                <Pill variant={variant}>
                  <Pill.Text fontType="text">{label}</Pill.Text>
                </Pill>
              </button>
            </Cell>
          );
        },
        sortingFn: (rowA, rowB) => {
          const getOrder = (mod: Module) => {
            if (mod.spec === null) return 0;
            if (isEqual(mod.spec, mod.desiredSpec)) return 1;
            return 2;
          };
          return getOrder(rowA.original) - getOrder(rowB.original);
        },
        enableSorting: true,
        meta: { displayName: "Spec" },
      }),
      columnHelper.accessor((row) => (row.spec ?? row.desiredSpec).version, {
        id: "version",
        header: (info) => <SortableHeader info={info}>Version</SortableHeader>,
        cell: (info) => (
          <Cell className="px-4" info={info}>
            <span className="inline-flex items-center gap-1">
              <code className="rounded bg-gray-200 px-2 py-1 font-code-12 text-gray-800">
                {info.getValue()}
              </code>
              <span onClick={(e) => e.stopPropagation()}>
                <CopyTextIcon
                  feedback="inline"
                  size="xs"
                  tooltip="Copy version"
                  value={info.getValue()}
                />
              </span>
            </span>
          </Cell>
        ),
        enableSorting: true,
        enableColumnFilter: false,
        meta: { displayName: "Version" },
      }),
      ...allConfigKeys.map((key) =>
        columnHelper.display({
          id: `config_${key}`,
          header: (info) => (
            <SortableHeader info={info}>
              {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
            </SortableHeader>
          ),
          cell: (info) => {
            const mod = info.row.original;
            const configValue = (mod.spec ?? mod.desiredSpec).config[key];
            return (
              <Cell className="px-4" info={info}>
                <ConfigValueCell value={configValue} />
              </Cell>
            );
          },
          enableSorting: false,
          meta: {
            displayName: key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase()),
          },
        }),
      ),
      columnHelper.display({
        id: "actions",
        header: () => <div className="w-10 px-4 py-3" />,
        cell: (info) => {
          const mod = info.row.original;
          return (
            <Cell className="px-4" info={info}>
              <Button
                size="sm"
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  openCreateOpModal(mod);
                }}
              >
                <Icon icon={faCog} size="sm" />
              </Button>
            </Cell>
          );
        },
        meta: { displayName: "Actions", sticky: "right" as const },
      }),
    ],
    [workspaceMap, orgMap, openDiffModal, openCreateOpModal, allConfigKeys],
  );

  const createOpContext = createOpModule
    ? getModuleContext(createOpModule)
    : { workspaceName: undefined, orgName: undefined };

  const handleRefreshAll = async () => {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: moduleKeys.all() });
    } finally {
      setIsRefreshing(false);
    }
  };

  if (filteredModules.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-end gap-2">
          <Button
            iconLeft={faRefresh}
            loading={isRefreshing}
            size="sm"
            variant="secondary"
            onClick={handleRefreshAll}
          >
            Refresh
          </Button>
          <Button
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={openEnableModal}
          >
            Enable Module
          </Button>
        </div>
        <div className="py-8 text-center font-inter-normal-13px text-gray-500">
          {searchTerm
            ? `No modules matching "${searchTerm}"`
            : "No modules of this kind"}
        </div>
        <EnableModuleModal
          afterLeave={afterEnableLeave}
          agentWorkspaceIds={agentWorkspaceIds}
          existingWorkspaceIds={existingWorkspaceIds}
          isOpen={isEnableOpen}
          moduleKind={moduleKind}
          orgMap={orgMap}
          workspaces={workspaces}
          onClose={closeEnableModal}
        />
        <CreateOperationModal
          afterLeave={afterCreateOpLeave}
          isOpen={isCreateOpOpen}
          module={createOpModule}
          orgName={createOpContext.orgName}
          workspaceName={createOpContext.workspaceName}
          onClose={closeCreateOpModal}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="font-inter-normal-13px text-gray-500">
          {filteredModules.length} module
          {filteredModules.length !== 1 ? "s" : ""}
        </span>
        <div className="flex items-center gap-2">
          <Button
            iconLeft={faRefresh}
            loading={isRefreshing}
            size="sm"
            variant="secondary"
            onClick={handleRefreshAll}
          >
            Refresh
          </Button>
          <Button
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={openEnableModal}
          >
            Enable Module
          </Button>
        </div>
      </div>
      <ModuleVersionBars
        modules={searchFilteredModules}
        selectedFilter={versionFilter}
        onSelectFilter={setVersionFilter}
      />
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <TableComp
          clientSideFiltering
          columns={columns}
          data={filteredModules}
          disableVirtualization
          getRowId={(row) => row.id}
          rowPropsGetter={(row) => ({
            onClick: () => openOpsDetailModal(row.original),
            classNameOverrides: twJoin(
              "cursor-pointer",
              searchTerm &&
                moduleHasOperationMatch(row.original, searchTerm) &&
                "bg-indigo-50/50",
            ),
          })}
          variant="default"
        />
        <SpecDiffModal
          afterLeave={afterDiffLeave}
          isOpen={isDiffOpen}
          module={diffModule}
          onClose={closeDiffModal}
        />
      </div>
      <EnableModuleModal
        afterLeave={afterEnableLeave}
        agentWorkspaceIds={agentWorkspaceIds}
        existingWorkspaceIds={existingWorkspaceIds}
        isOpen={isEnableOpen}
        moduleKind={moduleKind}
        orgMap={orgMap}
        workspaces={workspaces}
        onClose={closeEnableModal}
      />
      <CreateOperationModal
        afterLeave={afterCreateOpLeave}
        isOpen={isCreateOpOpen}
        module={createOpModule}
        orgName={createOpContext.orgName}
        workspaceName={createOpContext.workspaceName}
        onClose={closeCreateOpModal}
      />
      <Modal
        afterLeave={afterOpsDetailLeave}
        open={isOpsDetailOpen}
        size="lg"
        onClose={closeOpsDetailModal}
      >
        <Modal.Header
          description={
            currentOpsDetailModule
              ? [
                  opsDetailContext.orgName,
                  opsDetailContext.workspaceName,
                  currentOpsDetailModule.kind,
                ]
                  .filter(Boolean)
                  .join(" · ")
              : undefined
          }
        >
          Module Operations
        </Modal.Header>
        <Modal.Content>
          {currentOpsDetailModule && (
            <ModuleOperationsDetail
              module={currentOpsDetailModule}
              orgName={opsDetailContext.orgName}
              workspaceName={opsDetailContext.workspaceName}
              onCreateOperation={() => {
                closeOpsDetailModal();
                openCreateOpModal(currentOpsDetailModule);
              }}
            />
          )}
        </Modal.Content>
      </Modal>
    </div>
  );
};
